import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { publicTableService } from '../services/api';
import { useSocketEvents } from '../hooks/useSocket';
import type { CartItem } from '../contexts/CartContext';

const slideUp = keyframes`from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; }`;

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1200;
  display: flex; align-items: flex-end; justify-content: center;
`;
const Panel = styled.div`
  background: white; border-radius: 20px 20px 0 0; width: 100%; max-width: 600px;
  max-height: 90vh; display: flex; flex-direction: column;
  animation: ${slideUp} 0.25s ease;
`;
const PanelHeader = styled.div`
  padding: 20px 20px 12px; border-bottom: 1px solid #f3f4f6;
  display: flex; align-items: center; justify-content: space-between;
`;
const PanelTitle = styled.h2`font-size: 18px; font-weight: 700; color: #111827; margin: 0; display: flex; align-items: center; gap: 9px;`;
const TableTag = styled.div`font-size: 12px; color: #9ca3af; margin-top: 2px;`;
const CloseBtn = styled.button`
  background: #f3f4f6; border: none; border-radius: 50%; width: 32px; height: 32px;
  font-size: 16px; cursor: pointer; display: flex; align-items: center; justify-content: center;
`;
const Body = styled.div`flex: 1; overflow-y: auto; padding: 16px 20px;`;
const Footer = styled.div`padding: 16px 20px; border-top: 1px solid #f3f4f6;`;

const SubLabel = styled.div`font-size: 11px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; margin: 6px 0 4px;`;
const Row = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 10px 0; border-bottom: 1px solid #f9fafb;
  &:last-child { border-bottom: none; }
`;
const Name = styled.div`flex: 1; font-size: 14px; font-weight: 500; color: #111827;`;
const Price = styled.div`font-size: 14px; color: #6b7280; min-width: 60px; text-align: right;`;
const QtyControl = styled.div`display: flex; align-items: center; gap: 8px;`;
const QtyBtn = styled.button`
  width: 28px; height: 28px; border-radius: 50%; border: 1px solid #d1d5db;
  background: white; font-size: 16px; cursor: pointer; display: flex; align-items: center; justify-content: center;
  &:hover { background: #f3f4f6; }
`;
const QtyNum = styled.span`font-size: 14px; font-weight: 600; min-width: 20px; text-align: center;`;
const Dot = styled.span<{ $s: string }>`
  width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
  background: ${p => p.$s === 'ready' ? '#22c55e' : p.$s === 'preparing' ? '#f97316' : '#fbbf24'};
`;
const TotalRow = styled.button`
  width: 100%; border: none; background: #f9fafb; cursor: pointer;
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 14px; margin: 12px 0; border-radius: 10px;
  font-size: 15px; font-weight: 700; color: #1f2937;
`;
const TotalAmount = styled.span`font-size: 17px; font-weight: 800; color: #cc9d6d;`;
const TotalHint = styled.span`font-size: 13px; font-weight: 500; color: #9ca3af;`;
const SendBtn = styled.button`
  width: 100%; padding: 14px; background: #22c55e; color: white; border: none;
  border-radius: 12px; font-size: 16px; font-weight: 700; cursor: pointer;
  &:hover { background: #16a34a; }
  &:disabled { background: #d1d5db; cursor: not-allowed; }
`;
const EmptyMsg = styled.div`text-align: center; color: #9ca3af; padding: 28px 0; font-size: 14px;`;

const statusLabel: Record<string, string> = { pending: 'In attesa', preparing: 'In preparazione', ready: 'Pronto', served: 'Servito' };

interface SentItem { id: number; product_name: string; product_price: number; quantity: number; status: string; course?: number; }

interface Props {
  uuid: string;
  tableLabel: string;
  items: CartItem[];
  onUpdateQty: (lineId: string, qty: number) => void;
  onClearCart: () => void;
  onClose: () => void;
}

const TableCart: React.FC<Props> = ({ uuid, tableLabel, items, onUpdateQty, onClearCart, onClose }) => {
  const { on } = useSocketEvents();
  const [sent, setSent] = useState<SentItem[]>([]);
  const [maxCourse, setMaxCourse] = useState(0);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [showTotal, setShowTotal] = useState(false);
  const [sending, setSending] = useState(false);

  const loadOrder = useCallback(async () => {
    try {
      const res = await publicTableService.getTable(uuid);
      if (res.success && res.data?.order) {
        const allItems: SentItem[] = res.data.order.items || [];
        setOrderId(res.data.order.id);
        setSent(allItems.filter(i => i.status !== 'served'));
        setMaxCourse(allItems.reduce((m, i) => Math.max(m, i.course || 0), 0));
      } else {
        setSent([]);
        setMaxCourse(0);
      }
    } catch { /* ignora */ }
  }, [uuid]);

  useEffect(() => { loadOrder(); }, [loadOrder]);
  useEffect(() => on('order:item_added', (d: any) => { if (!orderId || d.order_id === orderId) loadOrder(); }), [on, orderId, loadOrder]);
  useEffect(() => on('order:item_updated', () => loadOrder()), [on, loadOrder]);

  const cartItems = items.filter(i => i.quantity > 0);
  const cartTotal = cartItems.reduce((s, i) => s + (i.price || 0) * i.quantity, 0);
  const sentTotal = sent.reduce((s, i) => s + Number(i.product_price) * i.quantity, 0);
  const grandTotal = cartTotal + sentTotal;

  const handleSend = async () => {
    if (cartItems.length === 0) return;
    setSending(true);
    // Ogni invio del cliente diventa una nuova portata
    const course = maxCourse + 1;
    try {
      for (const it of cartItems) {
        await publicTableService.addItem(uuid, {
          product_id: it.id,
          product_name: it.name,
          product_price: it.price || 0,
          quantity: it.quantity,
          notes: it.notes,
          course,
        });
      }
      onClearCart();
      await loadOrder();
    } catch { /* ignora */ }
    finally { setSending(false); }
  };

  return (
    <Overlay onClick={onClose}>
      <Panel onClick={e => e.stopPropagation()}>
        <PanelHeader>
          <div>
            <PanelTitle>🧾 Il tuo ordine</PanelTitle>
            <TableTag>{tableLabel}</TableTag>
          </div>
          <CloseBtn onClick={onClose}>✕</CloseBtn>
        </PanelHeader>

        <Body>
          {cartItems.length === 0 && sent.length === 0 && (
            <EmptyMsg>Nessun piatto selezionato.<br />Torna al menu e aggiungi qualcosa!</EmptyMsg>
          )}

          {cartItems.length > 0 && (
            <>
              <SubLabel>Da inviare</SubLabel>
              {cartItems.map(item => (
                <Row key={item.lineId}>
                  <Name>
                    {item.name}
                    {item.notes && <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400, marginTop: 2 }}>📝 {item.notes}</div>}
                  </Name>
                  {item.price != null && <Price>{(item.price * item.quantity).toFixed(2)}€</Price>}
                  <QtyControl>
                    <QtyBtn onClick={() => onUpdateQty(item.lineId, item.quantity - 1)}>−</QtyBtn>
                    <QtyNum>{item.quantity}</QtyNum>
                    <QtyBtn onClick={() => onUpdateQty(item.lineId, item.quantity + 1)}>+</QtyBtn>
                  </QtyControl>
                </Row>
              ))}
            </>
          )}

          {sent.length > 0 && Array.from(new Set(sent.map(i => i.course || 1))).sort((a, b) => a - b).map(courseNum => (
            <React.Fragment key={`c${courseNum}`}>
              <SubLabel>Portata {courseNum} · già in cucina</SubLabel>
              {sent.filter(i => (i.course || 1) === courseNum).map(item => (
                <Row key={item.id}>
                  <Dot $s={item.status} title={statusLabel[item.status]} />
                  <Name>{item.product_name}</Name>
                  <Price>×{item.quantity} · {(Number(item.product_price) * item.quantity).toFixed(2)}€</Price>
                </Row>
              ))}
            </React.Fragment>
          ))}

          {(cartItems.length > 0 || sent.length > 0) && (
            <TotalRow onClick={() => setShowTotal(s => !s)}>
              <span>Totale</span>
              {showTotal
                ? <TotalAmount>{grandTotal.toFixed(2)}€</TotalAmount>
                : <TotalHint>Tocca per vedere il totale</TotalHint>}
            </TotalRow>
          )}
        </Body>

        <Footer>
          <SendBtn disabled={cartItems.length === 0 || sending} onClick={handleSend}>
            {sending ? 'Invio…' : cartItems.length > 0 ? `Invia ordine (${cartItems.reduce((s, i) => s + i.quantity, 0)})` : 'Niente da inviare'}
          </SendBtn>
        </Footer>
      </Panel>
    </Overlay>
  );
};

export default TableCart;
